<template>
    <v-expansion-panels
        v-model="expansionPanelsModel"
        elevation="1"
        :rounded="false"
        class="mb-1 mb-sm-2 mb-md-4 mb-lg-5 mb-xl-6 mb-xxl-7"
        color="primary"
        :min-height="52"
        :max-height="60"
    >
        <v-expansion-panel :min-height="52" :max-height="60">
            <v-expansion-panel-title
                color="primary"
                class="elevation-1 font-weight-bold"
                :data-test="getCollapseId"
            >
                <v-skeleton-loader
                    v-if="isLoading"
                    type="text"
                    :width="titleWidth"
                    :max-width="titleWidth"
                />
                <template v-else>
                    <div v-html="title" />
                    <slot name="badge" />
                </template>
            </v-expansion-panel-title>
            <v-expansion-panel-text class="pa-0">
                <slot />
            </v-expansion-panel-text>
        </v-expansion-panel>
    </v-expansion-panels>
</template>

<script lang="ts">
    import { computed, nextTick, onMounted, ref, watch } from 'vue'
    import { useStorage } from '@vueuse/core'

    export default {
        name: 'ContentCollapse',
        props: {
            title: {
                type: String,
                required: true
            },
            collapseKey: {
                type: String,
                required: true
            },
            isVisibleByDefault: {
                type: Boolean,
                required: false,
                default: false
            },
            isLoading: {
                type: Boolean,
                required: false,
                default: false
            }
        },
        setup(props) {
            const expansionPanelsModel = ref<number | null>(null)
            const titleLengthState = ref<string>('')
            const visibleState = ref<boolean>(false)

            const title = computed(() => props.title)
            const isLoading = computed(() => props.isLoading)
            const getCollapseId = computed(() => `collapse-${props.collapseKey}`)
            const getTitleLengthId = computed(() => `${getCollapseId.value}-title-width-in-px`)
            const titleWidth = computed(() =>
                titleLengthState.value ? titleLengthState.value : '100'
            )

            const storedTitleLength = useStorage(
                `lernleitstand-store-${getTitleLengthId.value}`,
                '100'
            )
            const storedVisibleState = useStorage(
                `lernleitstand-store-${getCollapseId.value}`,
                props.isVisibleByDefault
            )

            onMounted(() => {
                titleLengthState.value = storedTitleLength.value
                visibleState.value = storedVisibleState.value

                if (visibleState.value) {
                    expansionPanelsModel.value = 0
                }
            })

            watch(
                () => props.isLoading,
                (newValue) => {
                    if (!newValue) {
                        // Update der Titelbreite nach der nächsten DOM-Aktualisierung
                        nextTick(() => {
                            const titleWidth = document.querySelector(
                                '[data-test="collapse-${props.collapseKey}"]'
                            )
                            titleLengthState.value = titleWidth
                                ? `${titleWidth.getBoundingClientRect().width}`
                                : '100'
                        })
                    }
                }
            )

            watch(
                () => expansionPanelsModel.value,
                (newValue) => {
                    storedVisibleState.value = newValue === 0
                }
            )

            return {
                expansionPanelsModel,
                getCollapseId,
                titleWidth,
                title,
                isLoading
            }
        }
    }
</script>
